import React, { useEffect, useState } from 'react';
import { Stepper, Step, StepLabel, Grid, Typography, Tabs, Tab, Box, Button, IconButton } from '@mui/material';
import BillingForm from '../components/forms/BillingForm';
import PayerInfo from '../components/cart/PayerInfo';
import CartSummary from '../components/cart/CartSummary';
import LockIcon from '@mui/icons-material/Lock';
import ConsentBoxes from '../components/cart/ConsentBoxes';
import CustomSnackbar from '../components/elements/CustomSnackbar';
import completeOrder from '../hooks/cart/completeOrder';
import { useCart } from '../contexts/CartContext';
import { KEY_ORDER_TYPE_DEPOSIT, KEY_ORDER_TYPE_FULL } from '../constants';
import { useNavigate } from 'react-router-dom';
import PreferredPaymentMethod from '../components/cart/PreferredPaymentMethod';
import { OrderErrors, PaymentMethodEnum, PaymentMethodParams, PaymentMethodType } from '../types';
import { useUser } from '../contexts/UserContext';
import savePaymentMethod from '../hooks/user/savePaymentMethods';
import getPaymentMethods from '../hooks/user/getPaymentMethods';
import getUser from '../hooks/user/getUser';
import { methodDefaultValidateBrands } from '../hooks/cart/AcceptedPaymentMethodBrands';
import { getLocationId } from '../utils';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { capitalizeFirstLetter } from '../utils';

const Checkout: React.FC = () => {
    const { orderType, paymentMethod, applyProcessingFee, isApplyingProcessingFee, setCartItemsTotal, applyPaymentMethod, setEnrollmentDetails } = useCart();
    const [activeStep, setActiveStep] = useState(0);
    const [tabValue, setTabValue] = useState(0);
    const [allCheckboxesChecked, setAllCheckboxesChecked] = useState(false);
    const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: string, linkLabel?: string, linkOnClick?: () => void}>({ open: false, message: '', severity: 'info'});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { user } = useUser();
    const [billingInfo, setBillingInfo] = useState({
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        phone: '',
        email: '',
        savePreferred: true,
    });
    const [stripeData, setStripeData] = useState({ token: '', brand: '', tokenPreferred: '' });
    const [isPreferredMethodSelected, setIsPreferredMethodSelected] = useState(false);
    const { companyName } = usePublicSettings();

    const handlePreferredPaymentMethodSelected = (selected: boolean) => {
        setIsPreferredMethodSelected(selected);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleNext = (stripeData: { token: string; brand: string, tokenPreferred: string }) => {
        setStripeData(stripeData);
        let paymentMethodTypeIndex = Object.values(PaymentMethodType).indexOf(stripeData.brand.toUpperCase());
        if (paymentMethodTypeIndex === -1) { paymentMethodTypeIndex = 0; }
        applyProcessingFee(paymentMethodTypeIndex);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setIsPreferredMethodSelected(false);
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleTabChange = (_event: any, newValue: React.SetStateAction<number>) => {
        setTabValue(newValue);
        if (newValue === PaymentMethodEnum.BANK) { applyProcessingFee(PaymentMethodType.BANK); }
    };

    useEffect(() => {
        if (orderType != KEY_ORDER_TYPE_DEPOSIT && orderType != KEY_ORDER_TYPE_FULL) {
            navigate('/cart');
        }
        applyProcessingFee(paymentMethod.paymentType);
    }, []);

    const handlePurchase = async () => {
        if (!allCheckboxesChecked) {
            setSnackbar({ open: true, message: "Not all checkboxes are checked. Cannot proceed with the purchase.", severity: 'error' });
            return;
        }

        if (!isPreferredMethodSelected && (!billingInfo || !stripeData.token)) {
            setSnackbar({ open: true, message: "Please complete payment details", severity: 'error' });
            return;
        }

        if (!user || !user.payerId) {
            setSnackbar({ open: true, message: "You are not a payer, please contact Kindertales support.", severity: 'error' });
            return;
        }

        setLoading(true);

        const methodType = stripeData.token ? stripeData.brand.toUpperCase() : PaymentMethodType[paymentMethod.cardType];
        
        if (isPreferredMethodSelected && methodType !== "BANK") {
            const locationUUID = getLocationId();
            if (!locationUUID) {
                setSnackbar({ open: true, message: "Error validating saved payment method.", severity: 'error' });
                setLoading(false);
                return;
            }
            // make api call to check if it is accepted method
            const methodDefaultValidateResponse = await methodDefaultValidateBrands(locationUUID);
            if (methodDefaultValidateResponse.statusCode !== 200) {
                setSnackbar({ open: true, message: "Error validating saved payment method.", severity: 'error' });
                setLoading(false);
                return;
            }
            if(methodDefaultValidateResponse.data.invalidBrand) {
                setSnackbar({ open: true, message: `${companyName} does not accept ${capitalizeFirstLetter(methodDefaultValidateResponse.data.invalidBrand)} at this location.`, severity: 'error' });
                setLoading(false);
                return;
            }
        }

        try {
            const response = await completeOrder({
                isDeposit: orderType == KEY_ORDER_TYPE_DEPOSIT,
                payerId: user.payerId,
                token: stripeData.token ? stripeData.token : '',
                methodType,
            });

            if (response.success && response.enrollmentDetails) {
                setSnackbar({ open: true, message: "Purchase completed. Redirecting...", severity: 'success' });
                setCartItemsTotal(0);
                setEnrollmentDetails(response.enrollmentDetails);

                if (billingInfo.savePreferred && tabValue == 0 && !isPreferredMethodSelected) {
                    const paymentMethodDetails = {
                        payerId: user.payerId,
                        token: stripeData.tokenPreferred,
                        firstName: billingInfo.firstName,
                        lastName: billingInfo.lastName,
                        phone: billingInfo.phone,
                        address: billingInfo.address,
                        country: billingInfo.country,
                        province: billingInfo.state,
                        city: billingInfo.city,
                        zip: billingInfo.zipCode
                    };

                    const saveResponse = await savePaymentMethod(paymentMethodDetails);
                    if (!saveResponse.success) {
                        setSnackbar({ open: true, message: "Failed to save payment method.", severity: 'error' });
                    } else {
                        const paymentMethods = await getPaymentMethods();
                        if (paymentMethods && paymentMethods.data) {
                            applyPaymentMethod(paymentMethods.data as PaymentMethodParams);
                        }
                    }
                }

                await getUser();

                navigate('/thank-you');
            } else {
                if (response.status === 400) {
                    if (response?.message === OrderErrors.DUPLICATE_CHARGE_PREVENTED)
                        setSnackbar({ 
                            open: true, 
                            message: 'Cart contains items you have already purchased. Please contact us at support@kindertales.com or use the chat bubble.',
                            severity: 'error',
                        })
                    else
                        setSnackbar({ open: true, message: response.message || "Purchase failed. Please try again.", severity: 'error' });
                } else {
                        setSnackbar({ open: true, message: response?.message || "Purchase failed. Please try again.", severity: 'error' });
                }
            }
        } catch (error) {
            setSnackbar({ open: true, message: "An error occurred.", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setActiveStep(0);
        setIsPreferredMethodSelected(false);
    }, [tabValue]);

    useEffect(() => {
        if (paymentMethod.paymentType === PaymentMethodEnum.BANK) {
            setTabValue(1);
        }
    }, [paymentMethod.paymentType]);

    const steps = ['Payment & Billing', 'Review & Place Order'];

    return (
        <div className="event-page p-5 mb-20">
            <Grid container spacing={4} className="pt-20 pb-20 checkout">
                <Grid item xs={12} md={2}></Grid>
                <Grid item xs={12} md={4}>
                    <Grid item xs={12}>
                        <Typography variant="h4" align="center">
                            <IconButton className="">
                                <LockIcon />
                            </IconButton>
                            Checkout
                        </Typography>
                        <Stepper activeStep={activeStep} alternativeLabel className="pt-5 pb-5 stepper">
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <Tabs value={tabValue} onChange={handleTabChange} className="tabs" centered
                            TabIndicatorProps={{
                                style: {
                                    backgroundColor: "#328CBED9"
                                }
                            }}
                        >
                            <Tab label="Credit Card" />
                            {paymentMethod && paymentMethod.paymentType == PaymentMethodEnum.BANK && (<Tab label="ACH/PAD" />)}
                        </Tabs>
                        {tabValue === 0 && (
                            <div className="tab">
                                {activeStep === 0 && (<>
                                    {paymentMethod && paymentMethod.paymentType == PaymentMethodEnum.CARD && (<PreferredPaymentMethod
                                        paymentMethod={paymentMethod}
                                        onPaymentMethodSelected={handlePreferredPaymentMethodSelected}
                                    />
                                    )}
                                    {isPreferredMethodSelected && (
                                        <>
                                            <Grid item xs={12}>
                                                <Button variant="contained" color="primary" onClick={() => setActiveStep((prev) => prev + 1)} className="next-button">
                                                    Next
                                                </Button>
                                            </Grid>
                                        </>
                                    )}
                                    {!isPreferredMethodSelected && (
                                        <BillingForm
                                            billingInfo={billingInfo}
                                            setBillingInfo={setBillingInfo}
                                            handleNext={(stripeData: { token: string; brand: string; tokenPreferred: string; }) => handleNext(stripeData)}
                                        />
                                    )}
                                </>
                                )}
                                {activeStep === 1 && (
                                    <Grid item xs={12}>
                                        <Box>
                                            {!isPreferredMethodSelected && (<PayerInfo
                                                billingInfo={billingInfo}
                                            />)}
                                            <ConsentBoxes
                                                onCheckAll={(checked) => setAllCheckboxesChecked(checked)}
                                            />
                                            <Button variant="contained" color="primary" onClick={handleBack} className="next-button back">
                                                Back
                                            </Button>
                                            <Button variant="contained" color="primary" onClick={handlePurchase} className="next-button ml-fix" disabled={loading || isApplyingProcessingFee}>
                                                {!loading ? 'Purchase' : 'Loading'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                )}
                            </div>
                        )}
                        {tabValue === 1 && (
                            <div className="tab">
                                {activeStep === 0 && (
                                    <Grid item xs={12}>
                                        <div className="mt-10">
                                            {paymentMethod && paymentMethod.paymentType == PaymentMethodEnum.BANK && (<PreferredPaymentMethod
                                                paymentMethod={paymentMethod}
                                                onPaymentMethodSelected={handlePreferredPaymentMethodSelected}
                                            />)}
                                            <Button variant="contained" color="primary" onClick={() => setActiveStep((prev) => prev + 1)} disabled={!isPreferredMethodSelected} className="next-button">
                                                Next
                                            </Button>
                                        </div>
                                    </Grid>
                                )}
                                {activeStep === 1 && (
                                    <Grid item xs={12}>
                                        <Box>
                                            <ConsentBoxes
                                                onCheckAll={(checked) => setAllCheckboxesChecked(checked)}
                                            />
                                            <Button variant="contained" color="primary" onClick={handleBack} className="next-button back">
                                                Back
                                            </Button>
                                            <Button variant="contained" color="primary" onClick={handlePurchase} className="next-button ml-fix" disabled={loading}>
                                                {!loading ? 'Purchase' : 'Loading'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                )}
                            </div>
                        )}
                    </Grid>
                </Grid>
                <Grid item container xs={12} md={4}>
                    <Grid item xs={12} md={1}></Grid>
                    <Grid item xs={12} md={10}>
                        <CartSummary readonly={true} applyCouponCode={undefined} checkout={true} showPayInFullText={true} />
                    </Grid>
                    <Grid item xs={12} md={1}></Grid>
                </Grid>
                <Grid item xs={12} md={2}></Grid>
                <CustomSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    handleClose={handleCloseSnackbar}
                    linkLabel={snackbar.linkLabel ? snackbar.linkLabel : undefined}
                    linkOnClick={snackbar.linkOnClick ? snackbar.linkOnClick : undefined}
                />
            </Grid>
        </div>
    );
};

export default Checkout;
